import {
  Box,
  CircularProgress,
  Fade,
  Grid,
  makeStyles,
  TextField,
  Theme,
  useTheme,
} from '@material-ui/core';
import cx from 'classnames';
import * as R from 'ramda';
import React, {
  ChangeEvent,
  createRef,
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
} from 'react';

enum KeyCode {
  Backspace = 8,
  Left = 37,
  Up = 38,
  Right = 39,
  Down = 40,
}

const noop = () => {};

const EMPTY = '';
const DEFAULT_INPUTS_COUNT = 4;

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    position: 'relative',
  },
  input: {
    textAlign: 'center',
    fontSize: theme.typography.h5.fontSize,
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.text.secondary,
    height: theme.spacing(7),
    boxSizing: 'border-box',
  },
  wrapper: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    backgroundColor: `rgba(255, 255, 255, .8)`,
  },
  wrapperHidden: {
    display: 'none',
  },
}));

export interface VerificationCodeInputProps {
  className?: string;
  focused?: boolean;
  isFetching?: boolean;
  length?: number;
  onChange?: (value: string) => void;
  onComplete?: (value: string) => void;
  value?: string;
}

const VerificationCodeInput: FunctionComponent<VerificationCodeInputProps> = ({
  className,
  length: inputsCount = DEFAULT_INPUTS_COUNT,
  value: currentValue = EMPTY,
  isFetching = false,
  onChange = noop,
  onComplete = noop,
  focused = false,
}: VerificationCodeInputProps) => {
  const classes = useStyles();
  const theme = useTheme();

  const values = useMemo(
    () =>
      Object.assign(new Array(inputsCount).fill(EMPTY), currentValue.split('')),
    [inputsCount, currentValue]
  );
  const refs = useMemo(
    () =>
      Array.from(new Array(inputsCount).keys()).map(index =>
        createRef<HTMLInputElement>()
      ),

    [inputsCount]
  );

  const handleInputFocus = useCallback(
    (index: number) => () => {
      const ref = refs[index];
      if (ref.current) {
        ref.current.select();
      }
    },
    [refs]
  );

  const handleInputKeyDown = useCallback(
    (index: number) => (event: React.KeyboardEvent) => {
      const prevRef = refs[index - 1];
      const nextRef = refs[index + 1];
      const value = values[index];

      switch (event.keyCode) {
        case KeyCode.Backspace: {
          event.preventDefault();
          const nextValues = [...values];

          if (value) {
            nextValues[index] = EMPTY;
          } else if (prevRef) {
            nextValues[index - 1] = '';
            prevRef.current!.focus();
          }
          onChange(nextValues.join(''));
          break;
        }
        case KeyCode.Left: {
          event.preventDefault();
          if (prevRef && prevRef.current) {
            prevRef.current.focus();
          }
          break;
        }

        case KeyCode.Right: {
          event.preventDefault();
          if (nextRef && nextRef.current) {
            nextRef.current.focus();
          }
          break;
        }
        case KeyCode.Up:
        case KeyCode.Down:
          event.preventDefault();
      }
    },
    [refs, values, onChange]
  );

  const handleInputChange = useCallback(
    (index: number) => (event: ChangeEvent<HTMLInputElement>) => {
      const {value: inputValue} = event.target;

      const normalizedValue = inputValue.slice(0, values.length - index);

      const nextValues = [...values];
      nextValues.splice(
        index,
        normalizedValue.length,
        ...normalizedValue.split('')
      );

      const lastIndex = Math.min(
        values.length - 1,
        index + normalizedValue.length
      );

      const nextValue = nextValues.join('');

      const ref = refs[lastIndex];

      if (ref && ref.current) {
        ref.current.focus();
      }

      onChange(nextValue);

      const isCompleted =
        lastIndex === values.length - 1 && R.all(R.identity, nextValues);

      if (isCompleted) {
        if (ref.current) {
          ref.current.blur();
        }
        onComplete(nextValue);
      }
    },
    [refs, values, onChange, onComplete]
  );

  useEffect(() => {
    const [firstRef] = refs;
    let timer: any;

    if (focused && firstRef.current) {
      timer = setTimeout(() => {
        if (firstRef.current) {
          firstRef.current.focus();
        }
      }, theme.transitions.duration.standard);
    }

    return () => {
      clearTimeout(timer);
    };
  }, [refs, focused, theme.transitions.duration.standard]);

  return (
    <Grid
      container
      direction="row"
      justify="space-between"
      spacing={2}
      className={cx(classes.root, className)}
    >
      {values.map((value, index) => (
        <Grid item xs key={index}>
          <TextField
            inputProps={{
              className: classes.input,
              pattern: '[0-9]*',
            }}
            variant="outlined"
            inputRef={refs[index]}
            value={value}
            disabled={isFetching}
            onFocus={handleInputFocus(index)}
            onKeyDown={handleInputKeyDown(index)}
            onChange={handleInputChange(index)}
          />
        </Grid>
      ))}
      <Box
        className={cx(classes.wrapper, {
          [classes.wrapperHidden]: !isFetching,
        })}
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="center"
      >
        <Fade in={isFetching}>
          <CircularProgress size={theme.spacing(4)} />
        </Fade>
      </Box>
    </Grid>
  );
};

export default VerificationCodeInput;
